/**
 * VoiceLevelIndicator - Visual microphone level meter
 *
 * Renders a set of vertical bars whose height responds to the
 * normalised audio level (0-1) from useAudioLevel.  Gives the
 * patient immediate feedback that their voice is being picked up
 * and a rough sense of whether they're speaking loudly enough.
 *
 * Props:
 *   level          - number 0-1, current audio RMS level
 *   barCount       - number of bars (default 5)
 *   height         - overall height in px (default 28)
 *   color          - bar colour when active (default theme success)
 *   muted          - if true, show muted state
 *   userHasSpoken  - if true, user has already said something; when level is low show "Listening..." instead of "Speak now" / "Quiet — try speaking louder"
 */

import { tokens } from '@healthdesk/shared-utils';
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

const BAR_COUNT_DEFAULT = 5;
const HEIGHT_DEFAULT = 28;
const BRAND_MAROON = tokens.colors.msiRed.light;

function VoiceLevelIndicator({
  level = 0,
  barCount = BAR_COUNT_DEFAULT,
  height = HEIGHT_DEFAULT,
  color,
  muted = false,
  userHasSpoken = false,
}) {
  // Map the 0-1 level to bar height; no extra boost so near-silence stays low (useAudioLevel already gates noise floor)
  const boosted = useMemo(() => Math.min(1, level * 1.2), [level]);

  const bars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < barCount; i++) {
      // Each bar has a threshold; bars fill left-to-right as level rises
      const threshold = (i + 1) / barCount;
      const active = boosted >= threshold;
      // Partial fill for the "current" bar
      const fraction = active
        ? 1
        : Math.max(0, (boosted - (i / barCount)) * barCount);
      arr.push({ active, fraction });
    }
    return arr;
  }, [boosted, barCount]);

  const label = useMemo(() => {
    if (muted) return 'Muted';
    // After user has spoken, low level = we heard them / processing, not "speak up"
    if (userHasSpoken && level < 0.12) return 'Listening...';
    if (level < 0.04) return 'Speak now...';
    if (level < 0.12) return 'Quiet — try speaking louder';
    if (level < 0.35) return 'Good volume';
    return 'Great!';
  }, [level, muted, userHasSpoken]);

  const barColor = color || (level < 0.12 ? '#fb8c00' : '#43a047');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: 'rgba(0,0,0,0.04)',
        width: 'fit-content',
      }}
    >
      {muted ? (
        <MicOffIcon
          fontSize="small"
          sx={{ color: BRAND_MAROON, transition: 'color 0.2s' }}
        />
      ) : (
        <MicIcon
          fontSize="small"
          sx={{ color: barColor, transition: 'color 0.2s' }}
        />
      )}

      {/* Bars */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height }}>
        {bars.map((bar, i) => (
          <Box
            key={i}
            sx={{
              width: 4,
              borderRadius: 1,
              bgcolor: muted
                ? BRAND_MAROON
                : bar.fraction > 0
                  ? barColor
                  : 'action.disabledBackground',
              height: muted
                ? height * 0.2
                : Math.max(height * 0.15, bar.fraction * height),
              transition: 'height 0.08s ease-out, background-color 0.15s',
            }}
          />
        ))}
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: muted ? BRAND_MAROON : 'text.secondary',
          ml: 0.5,
          minWidth: 80,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default React.memo(VoiceLevelIndicator);
